import { Box, Stack, Typography } from '@mui/material'
import Drawer from '@mui/material/Drawer'
import { useQuery } from '@tanstack/react-query'
import { observer } from 'mobx-react'
import React, { FC } from 'react'
import { getImageSrc } from '../../../../helpers/getImageSrc'
import { isOperator } from '../../../../services/AccountService'
import { getInstalledOrders } from '../../../../services/ApiService'
import { getComp } from '../../../../utils/utils/DI'
import WarningPaper from '../Drawers/CreateServiceDrawer/WarningPaper'
import ContentTitle from './components/ContentTitle'
import DrawerHeader from './components/DrawerHeader'
import NotFoundSearchResultButtons from './components/NotFoundSearchResultButtons'
import SuccessSearchResultButtons from './components/SuccessSearchResultButtons'
import SuccessSearchResults from './components/SuccessSearchResults'
import {
  contentWrapperSx,
  notFoundResultsWrapper,
  searchResultTitleSx,
} from './consts/styles.consts'
import { createServiceRequestStore } from './stores/NewCreateServiceRequestStore'

interface NewSearchRequestResultsDrawerProps {}

const NewSearchRequestResultsDrawer: FC<
  NewSearchRequestResultsDrawerProps
> = ({}) => {
  const {
    isResultsDrawerOpen,
    setIsResultsDrawerOpen,
    searchParams,
    setIsDrawerOpen,
    setSearchParams,
    setSelectedRequest,
    setResetAll,
    setActiveServiceOrder,
  } = createServiceRequestStore
  const { formatCategory } = getComp('CategoriesStore')
  const { getInstallationReasonName } = getComp('InstallationReasonStore')
  const { shownSenders } = getComp('SendersStore')

  const { data } = useQuery({
    queryKey: ['create-request-search-data', searchParams],
    queryFn: () => getInstalledOrders(searchParams),
    enabled: !!Object.keys(searchParams).length,
    select: (data) =>
      data?.orders.map((order: Record<string, any>) => {
        return {
          ...order,
          preferentialCategories: formatCategory(order?.preferentialCategories),
          installationReasonId: getInstallationReasonName(
            order?.installationReasonId
          ),
          senderId:
            shownSenders?.find((s: any) => s.id === order.senderId)?.name ?? '',
        }
      }),
  })

  const onBack = () => {
    setIsResultsDrawerOpen(false)
    setIsDrawerOpen(true)
    setSelectedRequest(null)
  }

  const onClose = () => {
    setIsResultsDrawerOpen(false)
    setSearchParams({})
    setActiveServiceOrder(null)
    setResetAll()
  }

  return (
    <Drawer
      anchor={'right'}
      open={isResultsDrawerOpen}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: 380,
        },
      }}
    >
      <DrawerHeader
        title={`Заявка на ${!isOperator() ? 'обслуживание' : 'ремонт'}`}
        onBack={onBack}
        onClose={onClose}
      />
      <Stack
        sx={contentWrapperSx}
        spacing={'14px'}
        justifyContent={'space-between'}
      >
        <Stack spacing={'14px'}>
          <ContentTitle
            title={'Результаты поиска'}
            iconSrc={getImageSrc('search')}
            styles={searchResultTitleSx}
            warn
          />
          {!!data?.length && <SuccessSearchResults results={data} />}
          {!data?.length && (
            <Box sx={notFoundResultsWrapper}>
              <Typography sx={{ fontWeight: '500' }}>
                Ничего не найдено
              </Typography>
              <WarningPaper
                title={'Поиск не дал результатов'}
                text1={
                  'Пожалуйста, проверьте правильность ввода данных Абонента и повторите поиск. '
                }
                text2={
                  'Если данные верные и поиск не дал результатов, пожалуйста, заведите заявку с новыми данными и сообщите Абоненту, что обработка и обслуживание такой заявки может потребовать дополнительного времени.'
                }
                style={null}
              />
            </Box>
          )}
        </Stack>
        <Stack sx={{ paddingX: '16px' }} spacing={'14px'}>
          {!!data?.length && (
            <SuccessSearchResultButtons onBack={onBack} onClose={onClose} />
          )}
          {!data?.length && <NotFoundSearchResultButtons onBack={onBack} />}
        </Stack>
      </Stack>
    </Drawer>
  )
}

export default observer(NewSearchRequestResultsDrawer)
